import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Trash2, QrCode, Car, LogOut, RefreshCw, Copy, Check, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('tago_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Dashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ label: '', plate_number: '', phone: '', country_code: '+973' });
  const [addLoading, setAddLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vehicles`, { withCredentials: true, headers: getHeaders() });
      setVehicles(res.data);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/login'); return; }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !user) { navigate('/login'); return; }
    if (user) fetchVehicles();
  }, [user, authLoading, navigate, fetchVehicles]);

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    if (!newVehicle.label.trim() || !newVehicle.phone.trim()) return;
    setAddLoading(true);
    try {
      await axios.post(`${API}/vehicles`, newVehicle, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      setNewVehicle({ label: '', plate_number: '', phone: '', country_code: '+973' });
      setShowAddForm(false);
      fetchVehicles();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add vehicle');
    } finally { setAddLoading(false); }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Delete this vehicle and its QR code?')) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, { withCredentials: true, headers: getHeaders() });
      fetchVehicles();
    } catch (err) { alert('Failed to delete'); }
  };

  const handleDownloadPDF = (vehicleId, format = 'print') => {
    const token = localStorage.getItem('tago_token');
    window.open(`${API}/vehicles/${vehicleId}/qr-pdf?token=${token}&format=${format}`, '_blank');
  };

  const handleCopyScanLink = (scanId) => {
    const url = `${window.location.origin}/scan/${scanId}`;
    navigator.clipboard.writeText(url);
    setCopied(scanId);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#040F1E] flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-[#00C4B4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040F1E] text-white font-dm">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .font-syne { font-family: 'Syne', sans-serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
      `}</style>

      {/* Header */}
      <div className="bg-[#0C2040] border-b border-[rgba(0,196,180,.2)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-syne text-3xl font-extrabold text-[#00C4B4]" style={{ letterSpacing: '-1.5px' }}>TAGO</a>
            <span className="text-white/30 hidden sm:block">|</span>
            <span className="text-white/70 font-semibold hidden sm:block">My Vehicles</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-white">{user?.name}</p>
              <p className="text-xs text-white/50">{user?.email}</p>
            </div>
            <button data-testid="dashboard-logout-button" onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 bg-[#040F1E] hover:bg-[#081828] border border-[rgba(0,196,180,.2)] rounded-lg text-sm text-white/70 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome + Add Button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-syne text-2xl sm:text-3xl font-extrabold text-white">Your Vehicles</h1>
            <p className="text-white/50 text-sm mt-1">Generate QR codes for your vehicles. Print and stick on your car.</p>
          </div>
          <button
            data-testid="add-vehicle-button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-5 py-3 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-bold rounded-xl transition-all hover:shadow-lg"
          >
            <Plus className="w-4 h-4" /> Add Vehicle
          </button>
        </div>

        {/* Add Vehicle Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4">
            <div className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-8 max-w-md w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-syne text-xl font-bold text-white">Add New Vehicle</h2>
                <button onClick={() => setShowAddForm(false)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleAddVehicle}>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-white/70 mb-2">Vehicle Name *</label>
                  <input
                    data-testid="vehicle-label-input"
                    type="text" value={newVehicle.label}
                    onChange={(e) => setNewVehicle({ ...newVehicle, label: e.target.value })}
                    placeholder='e.g. "My BMW", "Family Car"'
                    className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#00C4B4] transition-colors"
                    required autoFocus
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-white/70 mb-2">Your Mobile Number *</label>
                  <div className="flex gap-2">
                    <select
                      data-testid="vehicle-country-code"
                      value={newVehicle.country_code}
                      onChange={(e) => setNewVehicle({ ...newVehicle, country_code: e.target.value })}
                      className="w-28 bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl px-2 py-3 text-sm text-white focus:outline-none focus:border-[#00C4B4]"
                    >
                      <option value="+973">+973</option>
                      <option value="+971">+971</option>
                      <option value="+966">+966</option>
                      <option value="+974">+974</option>
                      <option value="+91">+91</option>
                    </select>
                    <input
                      data-testid="vehicle-phone-input"
                      type="tel" value={newVehicle.phone}
                      onChange={(e) => setNewVehicle({ ...newVehicle, phone: e.target.value })}
                      placeholder="e.g. 33445566"
                      className="flex-1 bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#00C4B4] transition-colors"
                      required
                    />
                  </div>
                  <p className="text-white/40 text-xs mt-1.5">This number will be shown when someone scans your QR code</p>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-white/70 mb-2">Plate Number (optional)</label>
                  <input
                    data-testid="vehicle-plate-input"
                    type="text" value={newVehicle.plate_number}
                    onChange={(e) => setNewVehicle({ ...newVehicle, plate_number: e.target.value })}
                    placeholder="e.g. 12345"
                    className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#00C4B4] transition-colors"
                  />
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowAddForm(false)} className="flex-1 px-4 py-3 bg-[#040F1E] border border-[rgba(0,196,180,.2)] text-white/70 font-bold rounded-xl transition-colors hover:text-white">Cancel</button>
                  <button data-testid="save-vehicle-button" type="submit" disabled={addLoading} className="flex-1 px-4 py-3 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-bold rounded-xl transition-all disabled:opacity-50">
                    {addLoading ? 'Adding...' : 'Add Vehicle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Vehicle Cards */}
        {vehicles.length === 0 ? (
          <div data-testid="empty-vehicles" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-12 text-center">
            <Car className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="font-syne text-xl font-bold text-white mb-2">No vehicles yet</h3>
            <p className="text-white/50 text-sm mb-6">Add your first vehicle to generate a QR code</p>
            <button onClick={() => setShowAddForm(true)} className="px-6 py-3 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-bold rounded-xl transition-all">
              <Plus className="w-4 h-4 inline mr-2" /> Add Your First Vehicle
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {vehicles.map((v) => (
              <div key={v.vehicle_id} data-testid={`vehicle-card-${v.vehicle_id}`} className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl overflow-hidden hover:border-[rgba(0,196,180,.4)] transition-colors">
                {/* QR Preview */}
                <div className="bg-white p-6 flex items-center justify-center">
                  <img
                    src={`${API}/vehicles/${v.vehicle_id}/qr?token=${localStorage.getItem('tago_token')}`}
                    alt="QR Code"
                    className="w-40 h-40 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>

                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-syne text-lg font-bold text-white">{v.label}</h3>
                      {v.plate_number && <p className="text-white/50 text-sm">Plate: {v.plate_number}</p>}
                    </div>
                    <div className="flex items-center gap-1 bg-[#00C4B4]/10 border border-[#00C4B4]/20 rounded-full px-3 py-1">
                      <QrCode className="w-3 h-3 text-[#00C4B4]" />
                      <span className="text-xs text-[#00C4B4] font-semibold">{v.scan_count} scans</span>
                    </div>
                  </div>

                  <p className="text-white/50 text-xs mb-4">Phone: {v.country_code} {v.phone}</p>

                  <div className="flex gap-2">
                    <button
                      data-testid={`download-print-${v.vehicle_id}`}
                      onClick={() => handleDownloadPDF(v.vehicle_id, 'print')}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-bold text-sm rounded-xl transition-all"
                      title="Half-page print with QR code"
                    >
                      <Download className="w-4 h-4" /> Print
                    </button>
                    <button
                      data-testid={`download-sticker-${v.vehicle_id}`}
                      onClick={() => handleDownloadPDF(v.vehicle_id, 'sticker')}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#FF6B35] hover:bg-[#FF8555] text-white font-bold text-sm rounded-xl transition-all"
                      title="90mm x 90mm car sticker"
                    >
                      <Download className="w-4 h-4" /> Sticker
                    </button>
                    <button
                      data-testid={`copy-link-${v.vehicle_id}`}
                      onClick={() => handleCopyScanLink(v.scan_id)}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#040F1E] border border-[rgba(0,196,180,.2)] text-white/70 hover:text-white font-bold text-sm rounded-xl transition-all"
                    >
                      {copied === v.scan_id ? <Check className="w-4 h-4 text-[#00C4B4]" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button
                      data-testid={`delete-vehicle-${v.vehicle_id}`}
                      onClick={() => handleDelete(v.vehicle_id)}
                      className="flex items-center justify-center px-3 py-2.5 bg-[#040F1E] border border-red-500/20 text-red-400 hover:bg-red-500/10 font-bold text-sm rounded-xl transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
